import React from 'react';
import { PersonaAvatar } from '../personas/PersonaAvatar';
import { getTierPresentation } from '../../utils/personaTier';
import { relativeTime } from '../../utils/relativeTime';
import type { Persona } from '../../types/persona';

interface PersonaPickerRowProps {
  persona: Persona;
  variant: 'compact' | 'full';
  /** Compact rows only: ISO timestamp of last chat. */
  lastActivity?: string;
  id: string;
  isHighlighted: boolean;
  onSelect: (persona: Persona) => void;
}

const MAX_CHIPS = 3;

export const PersonaPickerRow: React.FC<PersonaPickerRowProps> = ({
  persona, variant, lastActivity, id, isHighlighted, onSelect,
}) => {
  const { tier, glyph, ariaLabel, subtitleClass } = getTierPresentation(persona);
  const isBare = tier === 'bare';
  const tags = persona.tags || [];
  const visibleTags = tags.slice(0, MAX_CHIPS);
  const overflow = tags.length - visibleTags.length;

  const subtitle = isBare ? 'Bare persona — no Forge yet' : (persona.primarySpecialization || '');

  return (
    <div
      role="option"
      id={id}
      aria-selected={isHighlighted}
      onClick={() => onSelect(persona)}
      className={[
        'flex items-start gap-2 px-3 py-2 min-h-[44px] cursor-pointer rounded-md',
        isHighlighted ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50',
      ].join(' ')}
    >
      {/* Tier dot column */}
      <span className={`shrink-0 mt-0.5 text-sm leading-none ${isBare ? 'opacity-60' : ''}`}>
        <span aria-hidden="true">{glyph}</span>
        <span className="sr-only">{ariaLabel}</span>
      </span>

      {variant === 'full' && (
        <span className={`shrink-0 ${isBare ? 'opacity-60' : ''}`}>
          <PersonaAvatar name={persona.name} avatarUrl={persona.avatarUrl} specialization={persona.primarySpecialization} size="small" />
        </span>
      )}

      <span className="flex-1 min-w-0">
        <span className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate"
            title={persona.name} aria-label={persona.name}>
            {persona.name}
          </span>
          {variant === 'compact' && lastActivity && (
            <time dateTime={lastActivity} aria-label={`Last used ${relativeTime(lastActivity)}`}
              className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
              {relativeTime(lastActivity)}
            </time>
          )}
        </span>
        {/* tier subtitle color via module (bare → amber-700 5.9:1; else gray) */}
        <span className={`block text-xs truncate ${subtitleClass}`} title={subtitle}>
          {subtitle}
        </span>
        {variant === 'full' && visibleTags.length > 0 && (
          <span className="mt-1 flex flex-wrap gap-1">
            {visibleTags.map(t => (
              <span key={t} data-chip
                className="px-2 py-0.5 rounded-full text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                {t}
              </span>
            ))}
            {overflow > 0 && (
              <span className="px-1 py-0.5 text-[10px] text-gray-500 dark:text-gray-400">+{overflow}</span>
            )}
          </span>
        )}
      </span>
    </div>
  );
};
